/**
 * Unified API client with retry logic, caching, and logging
 * Encapsulates common patterns for all API requests
 */

import { logger } from './logger';
import { cache } from './cache';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const DEFAULT_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const BASE_DELAY = 1000; // 1 second
const MAX_DELAY = 10000; // 10 seconds

// HTTP status codes that should trigger a retry
const RETRYABLE_STATUS_CODES = [408, 429, 500, 502, 503, 504];

interface RequestOptions {
    timeout?: number;
    retry?: boolean;
    maxRetries?: number;
    cache?: boolean;
    cacheTTL?: number;
    headers?: Record<string, string>;
}

interface RequestConfig extends RequestOptions {
    method: string;
    url: string;
    body?: any;
}

class APIClient {
    private retryEnabled: boolean;

    constructor() {
        this.retryEnabled = process.env.REACT_APP_API_RETRY_ENABLED !== 'false';
    }

    /**
     * Calculate exponential backoff delay with jitter
     */
    private calculateBackoff(retryCount: number): number {
        const exponentialDelay = Math.min(BASE_DELAY * Math.pow(2, retryCount), MAX_DELAY);
        const jitter = Math.random() * 0.3 * exponentialDelay; // ±30% jitter
        return exponentialDelay + jitter;
    }

    /**
     * Check if HTTP status code should trigger a retry
     */
    private shouldRetry(status: number): boolean {
        return RETRYABLE_STATUS_CODES.includes(status);
    }

    /**
     * Sleep for specified duration
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Parse error from API response
     */
    private async parseError(response: Response): Promise<string> {
        try {
            const data = await response.json();

            // Handle structured error response
            if (data.detail) {
                if (typeof data.detail === 'string') {
                    return data.detail;
                } else if (typeof data.detail === 'object') {
                    const detail = data.detail;
                    if (detail.message) {
                        let message = detail.message;
                        if (detail.action) {
                            message += ` ${detail.action}`;
                        }
                        return message;
                    }
                    if (detail.error) {
                        return detail.error;
                    }
                }
            }

            if (data.message) {
                return data.message;
            }

            if (data.error) {
                if (typeof data.error === 'string') {
                    return data.error;
                }
                if (data.error.message) {
                    return data.error.message;
                }
            }

            return `Server error: ${response.status} ${response.statusText}`;
        } catch {
            return `Server error: ${response.status} ${response.statusText}`;
        }
    }

    /**
     * Execute fetch with timeout
     */
    private async fetchWithTimeout(
        url: string,
        options: RequestInit,
        timeout: number
    ): Promise<Response> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
            });
            clearTimeout(timeoutId);
            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error('Request timed out. Please check your connection and try again.');
            }
            throw error;
        }
    }

    /**
     * Execute request with retry logic
     */
    private async requestWithRetry(
        url: string,
        options: RequestInit,
        timeout: number,
        maxRetries: number
    ): Promise<Response> {
        let lastError: Error | null = null;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const response = await this.fetchWithTimeout(url, options, timeout);

                // If response is successful or non-retryable error, return it
                if (response.ok || !this.shouldRetry(response.status)) {
                    return response;
                }

                // If this is the last attempt, return the response
                if (attempt === maxRetries) {
                    return response;
                }

                // Log retry attempt
                logger.warn('Retrying request', {
                    url,
                    status: response.status,
                    attempt: attempt + 1,
                    maxRetries,
                });

                // Wait before retrying
                await this.delay(this.calculateBackoff(attempt));
            } catch (error) {
                lastError = error as Error;

                // If this is the last attempt, throw the error
                if (attempt === maxRetries) {
                    throw error;
                }

                // Log retry attempt
                logger.warn('Retrying request after error', {
                    url,
                    error: (error as Error).message,
                    attempt: attempt + 1,
                    maxRetries,
                });

                // Wait before retrying
                await this.delay(this.calculateBackoff(attempt));
            }
        }

        // This should never be reached, but TypeScript needs it
        throw lastError || new Error('Request failed after retries');
    }

    /**
     * Main request method
     */
    async request<T>(config: RequestConfig): Promise<T> {
        const {
            method,
            url,
            body,
            timeout = DEFAULT_TIMEOUT,
            retry = false,
            maxRetries = MAX_RETRIES,
            cache: useCache = false,
            cacheTTL = 60000,
            headers = {},
        } = config;

        const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

        // Check cache for GET requests
        if (method === 'GET' && useCache) {
            const cached = cache.get<T>(method, fullUrl);
            if (cached !== null) {
                return cached;
            }
        }

        // Prepare request options
        const requestOptions: RequestInit = {
            method,
            headers: {
                'Content-Type': 'application/json',
                ...headers,
            },
        };

        if (body) {
            requestOptions.body = JSON.stringify(body);
        }

        // Log request
        logger.logRequest(method, fullUrl, requestOptions);

        const startTime = Date.now();

        try {
            // Execute request with optional retry
            const response = this.retryEnabled && retry
                ? await this.requestWithRetry(fullUrl, requestOptions, timeout, maxRetries)
                : await this.fetchWithTimeout(fullUrl, requestOptions, timeout);

            const duration = Date.now() - startTime;

            // Log response
            logger.logResponse(method, fullUrl, response.status, response.statusText, duration);

            // Handle error responses
            if (!response.ok) {
                const errorMessage = await this.parseError(response);
                throw new Error(errorMessage);
            }

            // Parse response
            const data = await response.json();

            // Cache successful GET responses
            if (method === 'GET' && useCache) {
                cache.set(method, fullUrl, data, cacheTTL);
            }

            return data as T;
        } catch (error) {
            const duration = Date.now() - startTime;
            logger.logError(method, fullUrl, error as Error);
            logger.logResponse(method, fullUrl, 0, 'Error', duration);
            throw error;
        }
    }

    /**
     * GET request
     */
    async get<T>(url: string, options?: RequestOptions): Promise<T> {
        return this.request<T>({
            method: 'GET',
            url,
            ...options,
        });
    }

    /**
     * POST request
     */
    async post<T>(url: string, data?: any, options?: RequestOptions): Promise<T> {
        return this.request<T>({
            method: 'POST',
            url,
            body: data,
            ...options,
        });
    }

    /**
     * PUT request
     */
    async put<T>(url: string, data?: any, options?: RequestOptions): Promise<T> {
        return this.request<T>({
            method: 'PUT',
            url,
            body: data,
            ...options,
        });
    }

    /**
     * DELETE request
     */
    async delete<T>(url: string, options?: RequestOptions): Promise<T> {
        return this.request<T>({
            method: 'DELETE',
            url,
            ...options,
        });
    }

    /**
     * Clear cache entries matching a pattern
     */
    clearCache(pattern?: string): void {
        cache.clear(pattern);
    }

    /**
     * Get cache statistics
     */
    getCacheStats() {
        return cache.getStats();
    }
}

export const apiClient = new APIClient();
export type { RequestOptions };
